import { StrictMode } from 'react';
import { type Container, createRoot, type Root } from 'react-dom/client';
import './globals.css';
import './core/config/i18n';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './query/query-client';
import BaseLayout from '@/core/components/BaseLayout';
import App from './app';
import { ErrorBoundary } from '@/core/config/bugsnag';

const roots = new WeakMap<Element, Root>();

function mount(container: Element): Root {
	const root = createRoot(container as Container);
	roots.set(container, root);
	root.render(
		<ErrorBoundary>
			<StrictMode>
				<QueryClientProvider client={queryClient}>
					<BaseLayout>
						<App />
					</BaseLayout>
				</QueryClientProvider>
			</StrictMode>
		</ErrorBoundary>,
	);
	return root;
}

function unmount(container: Element): void {
	roots.get(container)?.unmount();
	roots.delete(container);
}

// A module executes once per document. Host pages that embed the shop inside a single-page app
// (the parent cancel flow) use this to mount it again on a later visit, or to mount the checkout
// after the shop, instead of reloading the bundle, which would create a second React copy next to
// the one the lazy chunks already hold.
window.fwJsShop = { mount, unmount };

const initialRoot = document.getElementById('root');
if (initialRoot) {
	mount(initialRoot);
}
